/**
 * Native IndexedDB store for MediaRecorder audio chunks (binary blobs).
 * Separate from RxDB `site_visit_recording_sessions` (metadata only).
 *
 * DevTools → Application → IndexedDB → `site_visit_recording_audio` → `chunks`
 */
import { assembleSiteVisitRecordingBlob } from '@services/siteVisitRecording/recordingMedia';

const DB_NAME = 'site_visit_recording_audio';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';

export interface SiteVisitRecordingChunkRecord {
	/** `${sessionId}:${chunkIndex}` */
	id: string;
	sessionId: string;
	chunkIndex: number;
	blob: Blob;
	mimeType: string;
	size: number;
	createdAt: number;
}

function chunkRecordId(sessionId: string, chunkIndex: number): string {
	return `${sessionId}:${chunkIndex}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openChunkDatabase(): Promise<IDBDatabase> {
	if (dbPromise) {
		return dbPromise;
	}
	dbPromise = new Promise((resolve, reject) => {
		const request = indexedDB.open(DB_NAME, DB_VERSION);
		request.onerror = (): void => reject(request.error ?? new Error('Failed to open recording chunk DB'));
		request.onsuccess = (): void => resolve(request.result);
		request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
			const db = (event.target as IDBOpenDBRequest).result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
				store.createIndex('sessionId', 'sessionId', { unique: false });
			}
		};
	});
	return dbPromise;
}

function runTransaction<T>(
	mode: IDBTransactionMode,
	run: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
	return openChunkDatabase().then(
		(db) =>
			new Promise<T | void>((resolve, reject) => {
				const tx = db.transaction(STORE_NAME, mode);
				const store = tx.objectStore(STORE_NAME);
				let request: IDBRequest<T> | undefined;
				try {
					const result = run(store);
					if (result) {
						request = result;
					}
				} catch (err) {
					reject(err instanceof Error ? err : new Error(String(err)));
					return;
				}
				tx.oncomplete = (): void => {
					if (request) {
						resolve(request.result);
					} else {
						resolve(undefined);
					}
				};
				tx.onerror = (): void => reject(tx.error ?? new Error('IndexedDB transaction failed'));
				tx.onabort = (): void => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
			})
	);
}

/** Append one MediaRecorder chunk for a session (call on each `dataavailable`). */
export async function persistRecordingChunk(
	sessionId: string,
	chunkIndex: number,
	blob: Blob,
	mimeType: string
): Promise<void> {
	const record: SiteVisitRecordingChunkRecord = {
		id: chunkRecordId(sessionId, chunkIndex),
		sessionId,
		chunkIndex,
		blob,
		mimeType: mimeType || blob.type || 'audio/webm',
		size: blob.size,
		createdAt: Date.now(),
	};
	await runTransaction('readwrite', (store) => store.put(record));
}

/** All chunks for a session, ordered by `chunkIndex`. */
export async function listRecordingChunks(sessionId: string): Promise<SiteVisitRecordingChunkRecord[]> {
	const db = await openChunkDatabase();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readonly');
		const store = tx.objectStore(STORE_NAME);
		const index = store.index('sessionId');
		const request = index.getAll(sessionId);
		request.onsuccess = (): void => {
			const rows = (request.result as SiteVisitRecordingChunkRecord[]) ?? [];
			rows.sort((a, b) => a.chunkIndex - b.chunkIndex);
			resolve(rows);
		};
		request.onerror = (): void => reject(request.error ?? new Error('Failed to list recording chunks'));
	});
}

export async function getRecordingChunksStats(sessionId: string): Promise<{ chunkCount: number; totalBytes: number }> {
	const chunks = await listRecordingChunks(sessionId);
	const contiguousCount = countContiguousRecordingChunksFromStart(chunks);
	const contiguousChunks = chunks.filter((c) => c.chunkIndex < contiguousCount);
	const totalBytes = contiguousChunks.reduce((sum, c) => sum + c.size, 0);
	return { chunkCount: contiguousCount, totalBytes };
}

/** Count chunks with indices 0, 1, 2, … without gaps (used for resume timing after reload). */
export function countContiguousRecordingChunksFromStart(chunks: SiteVisitRecordingChunkRecord[]): number {
	if (chunks.length === 0) {
		return 0;
	}
	const sorted = [...chunks].sort((a, b) => a.chunkIndex - b.chunkIndex);
	let count = 0;
	for (const chunk of sorted) {
		if (chunk.chunkIndex !== count) {
			break;
		}
		count += 1;
	}
	return count;
}

/** Merge persisted contiguous chunks into a single Blob (Phase 2 — used on Stop and for Phase 3 upload). */
export async function mergeRecordingChunksFromIdb(sessionId: string, mimeType: string): Promise<Blob> {
	const chunks = await listRecordingChunks(sessionId);
	const contiguousCount = countContiguousRecordingChunksFromStart(chunks);
	if (contiguousCount === 0) {
		return new Blob([], { type: mimeType || 'audio/webm' });
	}
	const contiguous = chunks.filter((c) => c.chunkIndex < contiguousCount).sort((a, b) => a.chunkIndex - b.chunkIndex);
	const type = mimeType || contiguous[0]?.mimeType || 'audio/webm';
	return assembleSiteVisitRecordingBlob(
		contiguous.map((c) => c.blob),
		type
	);
}

/** Remove all chunks for a session (e.g. after successful upload in Phase 3). */
export async function deleteRecordingChunksForSession(sessionId: string): Promise<void> {
	const chunks = await listRecordingChunks(sessionId);
	if (chunks.length === 0) {
		return;
	}
	const db = await openChunkDatabase();
	await new Promise<void>((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, 'readwrite');
		const store = tx.objectStore(STORE_NAME);
		for (const chunk of chunks) {
			store.delete(chunk.id);
		}
		tx.oncomplete = (): void => resolve();
		tx.onerror = (): void => reject(tx.error ?? new Error('Failed to delete recording chunks'));
	});
}
